import { format, parseISO, isValid } from 'date-fns'

/**
 * Проверяет, является ли дата «нулевой» или невалидной.
 * «Нулевой» считается дата `0001-01-01T00:00:00.000Z`
 *
 * @param date - Объект `Date` для проверки
 * @returns `true`, если дата невалидна или равна нулевой
 */
function isZeroDate (date: Date): boolean {
  return !isValid(date) || date.toISOString() === '0001-01-01T00:00:00.000Z'
}

/**
 * Форматирует дату в человекочитаемый формат `ДД.ММ.ГГГГ`.
 *
 * @param date - Объект `Date` или строка в формате ISO
 * @returns Строка в формате `ДД.ММ.ГГГГ` или пустая строка, если дата отсутствует или нулевая
 *
 * @example
 * formatDateHuman(new Date(2026, 4, 15)) // '15.05.2026'
 * formatDateHuman('2026-05-15')          // '15.05.2026'
 * formatDateHuman(null)                  // ''
 */
export function formatDateHuman (date: Date | string | null | undefined): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return date && !isZeroDate(parsedDate as Date) ? format(parsedDate as Date, 'dd.MM.yyyy') : ''
}

/**
 * Форматирует дату и время в человекочитаемый формат `ДД.ММ.ГГГГ ЧЧ:мм`.
 *
 * @param date - Объект `Date` или строка в формате ISO
 * @returns Строка в формате `ДД.ММ.ГГГГ ЧЧ:мм` или пустая строка, если дата отсутствует или нулевая
 *
 * @example
 * formatDateTimeHuman(new Date(2026, 4, 15, 9, 30)) // '15.05.2026 09:30'
 * formatDateTimeHuman('2026-05-15T09:30:00')        // '15.05.2026 09:30'
 * formatDateTimeHuman(null)                          // ''
 */
export function formatDateTimeHuman (date: Date | string | null | undefined): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return date && !isZeroDate(parsedDate as Date) ? format(parsedDate as Date, 'dd.MM.yyyy HH:mm') : ''
}

/**
 * Форматирует дату в формат ISO `ГГГГ-ММ-ДД`.
 *
 * @param date - Объект `Date` или строка в формате ISO
 * @returns Строка в формате `ГГГГ-ММ-ДД` или пустая строка, если дата отсутствует или нулевая
 *
 * @example
 * formatDateApi(new Date(2026, 4, 15)) // '2026-05-15'
 * formatDateApi('2026-05-15T09:30:00') // '2026-05-15'
 * formatDateApi(null)                  // ''
 */
export function formatDateApi (date: Date | string | null | undefined): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return date && !isZeroDate(parsedDate as Date) ? format(parsedDate as Date, 'yyyy-MM-dd') : ''
}

/**
 * Форматирует дату и время в формат ISO `ГГГГ-ММ-ДДTЧЧ:мм:сс`.
 *
 * @param date - Объект `Date` или строка в формате ISO
 * @returns Строка в формате `ГГГГ-ММ-ДДTЧЧ:мм:сс` или пустая строка, если дата отсутствует или нулевая
 *
 * @example
 * formatDateTimeApi(new Date(2026, 4, 15, 9, 30)) // '2026-05-15T09:30:00'
 * formatDateTimeApi('2026-05-15T09:30:00')        // '2026-05-15T09:30:00'
 * formatDateTimeApi(null)                          // ''
 */
export function formatDateTimeApi (date: Date | string | null | undefined): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return date && !isZeroDate(parsedDate as Date) ? format(parsedDate as Date, "yyyy-MM-dd'T'HH:mm:ss") : ''
}

/**
 * Разбирает дату в формате `ДД.ММ.ГГГГ` и возвращает объект `Date`.
 *
 * @param str - Строка в формате `ДД.ММ.ГГГГ`, например `'15.05.2026'`
 * @returns Объект `Date` или `null`, если строка не является корректной датой
 *
 * @example
 * parseHumanDate('15.05.2026') // Date(2026, 4, 15)
 * parseHumanDate('foo')        // null
 */
export const parseHumanDate = (str: string): Date | null => {
  const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
  if (!match) return null

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  if (year < 1) return null

  const date = new Date(0)
  date.setHours(0, 0, 0, 0)
  date.setFullYear(year, month - 1, day)

  return date.getDate() === day &&
    date.getMonth() === month - 1 &&
    date.getFullYear() === year
    ? date
    : null
}

/**
 * Разбирает дату в формате ISO `ГГГГ-ММ-ДД` и возвращает объект `Date`.
 *
 * @param str - Строка в формате `ГГГГ-ММ-ДД`, например `'2026-05-15'`
 * @returns Объект `Date` или `null`, если строка не является корректной датой
 *
 * @example
 * parseIsoDate('2026-05-15') // Date(2026, 4, 15)
 * parseIsoDate('foo')        // null
 */
export const parseIsoDate = (str: string): Date | null => {
  const d = new Date(str)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Разбирает период в формате `ГГГГ-ММ-ДД;ГГГГ-ММ-ДД` и возвращает пару дат.
 *
 * @param val - Строка в формате `ГГГГ-ММ-ДД;ГГГГ-ММ-ДД`, например `'2026-05-15;2026-05-22'`
 * @returns Кортеж `[начало, конец]` или `null`, если строка не соответствует формату
 *
 * @example
 * parsePeriodValue('2026-05-15;2026-05-22') // [Date(2026,4,15), Date(2026,4,22)]
 * parsePeriodValue('2026-05-15')            // null
 */
export const parsePeriodValue = (val: string): [Date, Date] | null => {
  const parts = val.split(';')
  if (parts.length !== 2) return null

  const [fromRaw, toRaw] = parts
  if (fromRaw === undefined || toRaw === undefined) return null
  const from = parseIsoDate(fromRaw)
  const to = parseIsoDate(toRaw)
  if (!from || !to) return null

  return [from, to]
}

/**
 * Разбирает период в формате `ДД.ММ.ГГГГ - ДД.ММ.ГГГГ` и возвращает пару дат.
 * Если конечная дата меньше начальной, даты меняются местами.
 *
 * @param val - Строка в формате `ДД.ММ.ГГГГ - ДД.ММ.ГГГГ`, например `'15.05.2026 - 22.05.2026'`
 * @returns Кортеж `[начало, конец]` или `null`, если строка не соответствует формату
 *
 * @example
 * parsePeriodHuman('15.05.2026 - 22.05.2026') // [Date(2026,4,15), Date(2026,4,22)]
 * parsePeriodHuman('22.05.2026 - 15.05.2026') // [Date(2026,4,15), Date(2026,4,22)] — даты переставлены
 * parsePeriodHuman('foo')                      // null
 */
export const parsePeriodHuman = (val: string): [Date, Date] | null => {
  const match = val.match(/^(\d{2}\.\d{2}\.\d{4}) - (\d{2}\.\d{2}\.\d{4})$/)
  if (!match) return null

  const fromRaw = match[1]
  const toRaw = match[2]
  if (fromRaw === undefined || toRaw === undefined) return null
  const from = parseHumanDate(fromRaw)
  const to = parseHumanDate(toRaw)
  if (!from || !to) return null

  return from <= to ? [from, to] : [to, from]
}
